import { useCallback } from 'react';
import {
  ACCRUED_DIVIDENDS_ROW_NUMBER,
  CUMULATIVE_DIVIDENDS_ROW_NUMBER,
  divRateLedgerInfluencedCellAliases,
  HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER,
} from 'common/constants/cap-table/capTable';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import calculateDividendsForMultipleDivRates from 'pages/CapTable/cap-table/utilities/calculateDividendsForMultipleDivRates';
import getLedgerInfluencedCells from 'pages/CapTable/cap-table/utilities/getLedgerInfluencedCells';
import { calculateCellChanges } from 'pages/CapTable/cap-table/utilities/variablePropertiesUtils';
import { useTableValidation } from 'services/hooks';
import { getStringValue, gridShortDate } from 'utilities';
import { ReverseParsedDividendRateLedgerItem, UseDividendRateLedgerActionsParams } from './types';

const useDividendRateLedgerActions = (params: UseDividendRateLedgerActionsParams) => {
  const { columns, setColumns, sheetsList, cellColumnLegend, closeDialog } = params;
  const { onCellsChanged: capTableOnCellsChanged, cells: capCells } = useFeatureSpreadsheet();
  const { validateCells } = useTableValidation();

  const addColumn = useCallback(() => {
    setColumns(prevState => [
      ...prevState,
      {
        id: null,
        name: `Rate ${columns.length + 1}`,
        effective_date: gridShortDate(),
        dividend_rate: '0',
        has_compounding: false,
        compounding_periods: null,
      },
    ]);
  }, [setColumns, columns]);

  const deleteColumn = useCallback(
    (columnIndex: number) => {
      const remainingColumns = columns.filter((_, index) => index !== columnIndex);
      setColumns(remainingColumns);
    },
    [setColumns, columns]
  );

  const calculateAccruedDividends = useCallback(() => {
    const spreadsheet = sheetsList[0];
    const items: ReverseParsedDividendRateLedgerItem[] = spreadsheet?.reverseParse();

    const { cellColumnRef, securities, measurementDate } = spreadsheet?.tableData ?? {};
    const baseSecurity = securities.find((security: any) => security.columnRef === cellColumnRef);
    return calculateDividendsForMultipleDivRates(items, baseSecurity, measurementDate);
  }, [sheetsList]);

  const saveLedger = useCallback(() => {
    const spreadsheet = sheetsList[0];
    const divRateLedgerItems: ReverseParsedDividendRateLedgerItem[] = spreadsheet?.reverseParse();

    const { areCellsValid: areLedgerCellsValid } = validateCells({
      cellsToValidate: Object.values(spreadsheet?.cells),
      customValidations: spreadsheet?.customValidations,
      parsedColumns: spreadsheet?.cells,
      useOriginalCell: true,
      tableData: spreadsheet?.tableData ?? {},
      isSaving: true,
    }) as { areCellsValid: boolean };

    if (!areLedgerCellsValid) {
      spreadsheet?.updateAllCellsDisplayConfig();
      return;
    }
    const ledgerInfluencedCells = getLedgerInfluencedCells(
      divRateLedgerInfluencedCellAliases,
      capCells,
      getStringValue(cellColumnLegend)
    );

    // find has dividends cell to set its value to 1 (true)
    // find multiple investments date cell and set value to 0 (false)
    const hasDividendsCell = capCells[`${cellColumnLegend}${CUMULATIVE_DIVIDENDS_ROW_NUMBER}`];
    const multipleInvestmentDatesCell = capCells[`${cellColumnLegend}${HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER}`];

    const accruedDividendsCell = capCells[`${cellColumnLegend}${ACCRUED_DIVIDENDS_ROW_NUMBER}`];
    const itemsWithDirtyAlias = divRateLedgerItems.map(item => ({
      ...item,
      compounding_period: item.compounding_periods,
    }));
    const cellChanges = calculateCellChanges(ledgerInfluencedCells, itemsWithDirtyAlias);

    capTableOnCellsChanged([
      {
        cell: hasDividendsCell,
        value: 1,
      },
      {
        cell: multipleInvestmentDatesCell,
        value: 0,
      },
      ...cellChanges,
      {
        cell: accruedDividendsCell,
        value: calculateAccruedDividends(),
      },
    ]);
    closeDialog();
  }, [
    sheetsList,
    closeDialog,
    capTableOnCellsChanged,
    cellColumnLegend,
    capCells,
    validateCells,
    calculateAccruedDividends,
  ]);

  return {
    addColumn,
    deleteColumn,
    saveLedger,
  };
};

export default useDividendRateLedgerActions;
